import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import {
  BackLink,
  ErrorNotice,
  FactList,
  LoadingState,
  PageHeading,
  Panel,
} from '@bcecloud/portal-ui';
import { errorMessage } from '@/lib/api';
import { formatEventDate } from '@/lib/event-time';
import { readRecap, type EventRecap } from '@/lib/events';
import { ClientFeedbackRow, RecapAnswerRow } from './event-tabs';

/** The facilitator's display name from a recap's populated ref. */
function facilitatorName(recap: EventRecap): string {
  const facilitator = recap.facilitator;
  if (typeof facilitator === 'object' && facilitator !== null) {
    return (
      [facilitator.firstName, facilitator.lastName].filter(Boolean).join(' ') ||
      facilitator.email ||
      'Facilitator'
    );
  }
  return 'Facilitator';
}

/**
 * One event recap on its own page — the classic portal's single-recap view,
 * back. The event's Recap tab remains the in-context way to read the same
 * thing; this page is where the recaps LIST lands, and what gets linked in
 * conversations ("see Russ's recap").
 *
 * @returns The recap detail screen.
 */
export function RecapDetailPage(): React.ReactElement {
  const { recapId } = useParams<{ recapId: string }>();
  const recap = useQuery({
    queryKey: ['recap', recapId],
    queryFn: ({ signal }) => readRecap(recapId ?? '', signal),
    enabled: recapId !== undefined,
  });

  if (recap.isPending) return <LoadingState label="Loading recap…" />;
  if (recap.isError) {
    return <ErrorNotice title="Recap unavailable" message={errorMessage(recap.error)} />;
  }

  const detail = recap.data;
  const event = typeof detail.event === 'object' ? detail.event : null;
  const submitted = detail.createdAt ? new Date(detail.createdAt).toLocaleDateString() : undefined;

  return (
    <section className="animate-portal-rise">
      <BackLink to="/recaps">All recaps</BackLink>

      <div className="mt-3">
        <PageHeading
          eyebrow="EVENT RECAP"
          size="compact"
          title={facilitatorName(detail)}
          {...(submitted ? { subtitle: `Submitted ${submitted}` } : {})}
        />
      </div>

      {/* The classic header facts: contract (linking home to the event),
          date, program, client. */}
      <Panel className="mt-4 p-5">
        <FactList
          columns={3}
          facts={[
            {
              label: 'Contract',
              value: event?._id ? (
                <Link
                  to={`/events/${event._id}?tab=recap`}
                  className="font-bold text-brand hover:underline"
                >
                  {event.contractNumber ?? 'Open event'}
                </Link>
              ) : (
                event?.contractNumber
              ),
            },
            { label: 'Event date', value: formatEventDate(event, 'medium') ?? undefined },
            { label: 'Program', value: event?.programName },
            { label: 'Client', value: event?.account?.name },
          ]}
        />
      </Panel>

      <Panel className="mt-4">
        {detail.clientFeedback?.trim() ? <ClientFeedbackRow feedback={detail.clientFeedback.trim()} /> : null}
        {detail.recap.map((answer) => (
          <RecapAnswerRow key={answer.name || answer.question} answer={answer} />
        ))}
      </Panel>
    </section>
  );
}
